/**
 * Landing-page section icon registry.
 *
 * Config items reference an icon by KEY (e.g. `icon: "cosmetics"`); components
 * resolve it here via `resolveIcon(key)`. This keeps configs readable, lets any
 * page reuse an icon by name, and lets non-devs pick icons without touching JSX.
 *
 * Values are string paths to SVGs in /public/assets/icons and are rendered as an
 * <img src>. The Figma uses a single generic "featured icon" inside a circle for
 * these bands, so several semantic keys currently point at shared generic glyphs;
 * swapping in bespoke topical icons later = change the path here only, no config
 * or component edits.
 */

const ICONS = {
  // generic / structural
  default: "/assets/icons/shield-tick.svg",
  location: "/assets/icons/globe-02.svg",
  check: "/assets/icons/check-circle.svg",
  quality: "/assets/icons/badge-check-green.svg",
  compliance: "/assets/icons/shield-tick.svg",
  logistics: "/assets/icons/truck.svg",
  drop: "/assets/icons/drop.svg",
  network: "/assets/icons/dataflow-04.svg",
  reliability: "/assets/icons/reliability.svg",
  scalability: "/assets/icons/scalability.svg",
  flexibility: "/assets/icons/flexibility.svg",

  // ⚠️ `fieldGrid` is no longer used by the hero enquiry form — the trailing glyph
  // was removed from every field (it read as a decorative dot-grid with no
  // meaning). Kept in the registry because it is still referenced by 18 config
  // entries, which now resolve to a glyph the form ignores; removing the key would
  // make those configs fall back to the DEFAULT icon rather than to nothing.
  fieldGrid: "/assets/icons/lp-field-grid.svg",

  // Magnifier for the product typeahead — the one field that DOES keep a trailing
  // icon, because there it means something: this input searches.
  search: "/assets/icons/lp-search.svg",

  // Bare green tick for the product hero's chip row (Figma 4672-17210, Colors/
  // Green #34C759). Supplied by the user as the exact asset; the pre-existing
  // check-circle-green.svg is a CIRCLED #22C55E glyph and is not the same mark.
  checkGreen: "/assets/icons/lp-check-green.svg",

  // outlined location pin used by the dark regional-network cards (grey stroke)
  locationPin: "/assets/icons/lp-location-pin.svg",
  // same pin stroked coral, for the LIGHT network band (chemical-toll 4674-18654)
  // where the glyph sits bare on white with no circular wrapper behind it
  locationPinCoral: "/assets/icons/lp-location-pin-coral.svg",
  // coral parallelogram bullet marker used by the "why choose" cards
  markerSlash: "/assets/icons/lp-marker-slash.svg",
  // white-60 blending/mixing glyph for the DARK partnerBand (chemical-toll).
  // Its fill is baked white — it is unreadable on a light surface, so light
  // bands must keep using `industry` (coral) instead.
  blending: "/assets/icons/lp-blending.svg",

  // industry / topical — the Figma icon+label bands use ONE shared glyph for
  // every item in the list, so all industry keys resolve to it. Distinct
  // per-industry art can be dropped in later by changing only these paths.
  industry: "/assets/icons/lp-industry.svg",
  cosmetics: "/assets/icons/lp-industry.svg",
  wellness: "/assets/icons/lp-industry.svg",
  food: "/assets/icons/lp-industry.svg",
  cleaning: "/assets/icons/lp-industry.svg",
  pharma: "/assets/icons/lp-industry.svg",
};

/** Resolve an icon key to its SVG path, falling back to the default glyph. */
export function resolveIcon(key) {
  return ICONS[key] ?? ICONS.default;
}

export default ICONS;
